import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { format, subDays } from "date-fns";

import ConfirmDialogBox from "../DailogBoxes/confirmdailogbox";
import AlertDialogBox from "../DailogBoxes/alertdailogbox";
import ErorrDialogBox from "../DailogBoxes/errordaologbox";
import Service from "../../Service/firebase";
import { settodaybooking } from "../../actions/settodaybookingaction";
import { isVendorRole, getVendorId } from "../../utils/rbac";

import "./todaybookinglist.css";

// ── helpers ──────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Completed: { bg: "#d4edda", color: "#155724" },
  Accepted:  { bg: "#cce5ff", color: "#004085" },
  Pending:   { bg: "#fff3cd", color: "#856404" },
  Cancelled: { bg: "#f8d7da", color: "#721c24" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#e9ecef", color: "#495057" };
  return (
    <span style={{
      fontSize: "11px", padding: "3px 8px", borderRadius: "10px",
      fontWeight: 600, background: s.bg, color: s.color,
    }}>
      {status || "—"}
    </span>
  );
};

const PayBadge = ({ status }) => (
  <span style={{
    fontSize: "11px", padding: "2px 7px", borderRadius: "8px", fontWeight: 600,
    background: status === "Paid" ? "#d4edda" : "#fff3cd",
    color: status === "Paid" ? "#155724" : "#856404",
  }}>
    {status || "—"}
  </span>
);

const WalkInBadge = ({ isWalkIn }) => (
  <span style={{
    fontSize: "10px", padding: "2px 6px", borderRadius: "6px",
    background: isWalkIn ? "#e2e3e5" : "#cce5ff",
    color: isWalkIn ? "#383d41" : "#004085", fontWeight: 600,
  }}>
    {isWalkIn ? "Walk-in" : "App"}
  </span>
);

// ── component ─────────────────────────────────────────────────────────────────
const TodayBookingList = () => {
  const vendorLocked = isVendorRole();
  const lockedVendorId = vendorLocked ? getVendorId() : null;

  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);   // client-side master copy
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadMoreData, setIsLoadMoreData] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [noMoreData, setNoMoreData] = useState(false);
  const [limit, setLimit] = useState(20);

  // filters
  const [filterStartDate, setFilterStartDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [filterEndDate, setFilterEndDate]     = useState(format(new Date(), "yyyy-MM-dd"));
  const [filterStatus, setFilterStatus]       = useState("All");
  const [filterPayMode, setFilterPayMode]     = useState("All");
  const [filterType, setFilterType]           = useState("All");   // All / WalkIn / App
  const [filterPayStatus, setFilterPayStatus] = useState("All");
  const [searchText, setSearchText]           = useState("");

  // dialog
  const [openConfirmDailog, setOpenConfirmDailog] = useState(false);
  const [openAlertDailog, setOpenAlertDailog]     = useState(false);
  const [openErrorDailog, setOpenErrorDailog]     = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState("");

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchBookings = useCallback(async (lim) => {
    setIsLoadMoreData(true);
    const data = vendorLocked
      ? await Service.getBookingsDataByVendorWithinDateRange(
          new Date(filterStartDate), new Date(filterEndDate),
          filterStatus, lim, lockedVendorId)
      : await Service.getBookingsDataWithinDateRange(
          new Date(filterStartDate), new Date(filterEndDate),
          filterStatus, lim);
    setAllBookings(data);
    setIsLoadMoreData(false);
    setIsLoading(false);
    if (data.length < lim) setNoMoreData(true);
    else setNoMoreData(false);
  }, [filterStartDate, filterEndDate, filterStatus, vendorLocked, lockedVendorId, limit]);

  useEffect(() => {
    setIsLoading(true);
    fetchBookings(limit);
  }, [filterStartDate, filterEndDate, filterStatus]);

  // ── client-side filters ────────────────────────────────────────────────────
  useEffect(() => {
    let filtered = [...allBookings];

    if (filterType === "WalkIn")  filtered = filtered.filter(b => b.isWalkIn === true);
    if (filterType === "App")     filtered = filtered.filter(b => !b.isWalkIn);
    if (filterPayMode !== "All")  filtered = filtered.filter(b => b.paymentMode === filterPayMode);
    if (filterPayStatus !== "All") filtered = filtered.filter(b => b.paymentStatus === filterPayStatus);

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(b =>
        (b.userName || "").toLowerCase().includes(q) ||
        (b.phone || "").includes(q) ||
        (b.bookingId || "").toLowerCase().includes(q) ||
        (b.salonName || "").toLowerCase().includes(q) ||
        (b.stylistName || "").toLowerCase().includes(q)
      );
    }

    setBookings(filtered);
  }, [allBookings, filterType, filterPayMode, filterPayStatus, searchText]);

  // ── stats ──────────────────────────────────────────────────────────────────
  const totalRevenue   = bookings.reduce((s, b) => s + (b.finalPrice || 0), 0);
  const completedCount = bookings.filter(b => b.status === "Completed").length;
  const pendingCount   = bookings.filter(b => b.status === "Pending" || b.status === "Accepted").length;
  const cancelledCount = bookings.filter(b => b.status === "Cancelled").length;

  const clearFilters = () => {
    setFilterType("All"); setFilterPayMode("All");
    setFilterPayStatus("All"); setSearchText("");
  };

  const handleShowMore = () => {
    const newLimit = limit + 20;
    setLimit(newLimit);
    fetchBookings(newLimit);
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return isLoading ? (
    <div className="bk_page"><i className="fas fa-spinner fa-pulse fa-2x"></i></div>
  ) : (
    <div className="bk_page">
      <ConfirmDialogBox
        openDailog={openConfirmDailog}
        onSetOpenDailog={() => setOpenConfirmDailog(false)}
        handleConfirmOkBtn={async () => {
          await Service.deleteData("appointments", selectedBookingId);
          setOpenConfirmDailog(false);
          setOpenAlertDailog(true);
          fetchBookings(limit);
        }}
        isLoading={false}
        title="Delete Booking"
        des="Are you sure you want to delete this booking?"
      />
      <AlertDialogBox openDailog={openAlertDailog} onSetOpenDailog={() => setOpenAlertDailog(false)} title="Deleted" des="Booking deleted." />
      <ErorrDialogBox openDailog={openErrorDailog} onSetOpenDailog={() => setOpenErrorDailog(false)} title="Error" des="Something went wrong." />

      {/* Header */}
      <div className="bk_header">
        <div>
          <h4 className="bk_title">Bookings</h4>
          <p className="bk_subtitle">{bookings.length} appointments</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bk_filter_bar">
        <div className="bk_filter_row">
          <div className="bk_filter_group">
            <label>From</label>
            <input type="date" value={filterStartDate}
              onChange={e => setFilterStartDate(e.target.value)} />
          </div>
          <div className="bk_filter_group">
            <label>To</label>
            <input type="date" value={filterEndDate}
              onChange={e => setFilterEndDate(e.target.value)} />
          </div>
          <div className="bk_filter_group">
            <label>Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="All">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Accepted">Accepted</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="bk_filter_group">
            <label>Type</label>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="All">All Types</option>
              <option value="WalkIn">Walk-in</option>
              <option value="App">App</option>
            </select>
          </div>
          <div className="bk_filter_group">
            <label>Payment</label>
            <select value={filterPayMode} onChange={e => setFilterPayMode(e.target.value)}>
              <option value="All">All Modes</option>
              <option value="Cash">Cash</option>
              <option value="Online">Online</option>
              <option value="UPI">UPI</option>
            </select>
          </div>
          <div className="bk_filter_group">
            <label>Pay Status</label>
            <select value={filterPayStatus} onChange={e => setFilterPayStatus(e.target.value)}>
              <option value="All">All</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
            </select>
          </div>
          <div className="bk_filter_group bk_search_group">
            <label>Search</label>
            <input type="text" placeholder="Name / phone / booking ID..."
              value={searchText} onChange={e => setSearchText(e.target.value)} />
          </div>
          <button className="bk_clear_btn" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bk_stats_row">
        <div className="bk_stat_card bk_stat_revenue">
          <div className="bk_stat_val">₹{totalRevenue.toLocaleString("en-IN")}</div>
          <div className="bk_stat_lbl">Total Revenue</div>
        </div>
        <div className="bk_stat_card bk_stat_completed">
          <div className="bk_stat_val">{completedCount}</div>
          <div className="bk_stat_lbl">Completed</div>
        </div>
        <div className="bk_stat_card bk_stat_pending">
          <div className="bk_stat_val">{pendingCount}</div>
          <div className="bk_stat_lbl">Pending / Accepted</div>
        </div>
        <div className="bk_stat_card bk_stat_cancelled">
          <div className="bk_stat_val">{cancelledCount}</div>
          <div className="bk_stat_lbl">Cancelled</div>
        </div>
        <div className="bk_stat_card">
          <div className="bk_stat_val">{bookings.length}</div>
          <div className="bk_stat_lbl">Shown</div>
        </div>
      </div>

      {/* Table */}
      {bookings.length === 0 ? (
        <div className="bk_empty">No bookings found for selected filters</div>
      ) : (
        <div className="bk_table_wrap">
          <table className="bk_table">
            <thead>
              <tr>
                <th>#</th>
                <th>Booking ID</th>
                <th>Date</th>
                <th>Time</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Type</th>
                {!vendorLocked && <th>Salon</th>}
                <th>Stylist</th>
                <th>Services</th>
                <th>Amount</th>
                <th>Discount</th>
                <th>Final</th>
                <th>Pay Mode</th>
                <th>Pay Status</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, idx) => {
                const isExpanded = expandedRow === b.bookingId;
                const serviceNames = (b.services || []).map(s => s.name).join(", ");
                const stylistNames = b.stylistBreakdown
                  ? Object.values(b.stylistBreakdown).map(s => s.name).join(", ")
                  : b.stylistName || "—";

                return (
                  <React.Fragment key={b.bookingId || idx}>
                    <tr className={isExpanded ? "bk_row_expanded" : ""}>
                      <td>{idx + 1}</td>
                      <td className="bk_id">{b.bookingId || "—"}</td>
                      <td>{b.scheduleDate || "—"}</td>
                      <td>{b.scheduleTime || "—"}</td>
                      <td className="bk_name">{b.userName || "—"}</td>
                      <td>{b.phone || "—"}</td>
                      <td><WalkInBadge isWalkIn={b.isWalkIn} /></td>
                      {!vendorLocked && <td style={{ fontSize: "12px" }}>{b.salonName || "—"}</td>}
                      <td style={{ fontSize: "12px" }}>{stylistNames}</td>
                      <td style={{ fontSize: "12px", maxWidth: "160px" }}>{serviceNames || "—"}</td>
                      <td>₹{b.TotalServicePrice ?? b.totalServicePrice ?? "—"}</td>
                      <td>{b.discountAmount ? `₹${b.discountAmount}` : "—"}</td>
                      <td className="bk_final">₹{b.finalPrice ?? "—"}</td>
                      <td style={{ fontSize: "12px" }}>{b.paymentMode || "—"}</td>
                      <td><PayBadge status={b.paymentStatus} /></td>
                      <td><StatusBadge status={b.status} /></td>
                      <td>
                        <button className="bk_expand_btn"
                          onClick={() => setExpandedRow(isExpanded ? null : b.bookingId)}>
                          <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr className="bk_detail_row">
                        <td colSpan={vendorLocked ? 16 : 17}>
                          <div className="bk_detail_panel">

                            {/* Services breakdown */}
                            <div className="bk_detail_section">
                              <h6>Services</h6>
                              <table className="bk_inner_table">
                                <thead>
                                  <tr>
                                    <th>Service</th>
                                    <th>Stylist</th>
                                    <th>Duration</th>
                                    <th>Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(b.services || []).map((svc, si) => (
                                    <tr key={si}>
                                      <td>{svc.name}</td>
                                      <td>{svc.stylistName || "—"}</td>
                                      <td>{svc.time ? `${svc.time} min` : "—"}</td>
                                      <td>₹{svc.price}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Stylist breakdown (walkin multi-stylist) */}
                            {b.stylistBreakdown && (
                              <div className="bk_detail_section">
                                <h6>Stylist Breakdown</h6>
                                <table className="bk_inner_table">
                                  <thead>
                                    <tr>
                                      <th>Stylist</th>
                                      <th>Services</th>
                                      <th>Revenue</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(b.stylistBreakdown).map(([sid, st]) => (
                                      <tr key={sid}>
                                        <td>{st.name}</td>
                                        <td>{st.serviceCount}</td>
                                        <td>₹{st.revenue}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Extra info */}
                            <div className="bk_detail_section bk_detail_meta">
                              <div><span>Booking ID</span><strong>{b.bookingId}</strong></div>
                              <div><span>Created</span><strong>{b.createdAt || "—"}</strong></div>
                              {!vendorLocked && <div><span>Salon</span><strong>{b.salonName}</strong></div>}
                              {!vendorLocked && <div><span>Address</span><strong>{b.salonAddress || "—"}</strong></div>}
                              <div><span>Home Order</span><strong>{b.homeOrder ? "Yes" : "No"}</strong></div>
                              {b.couponCode && <div><span>Coupon</span><strong>{b.couponCode}</strong></div>}
                              {b.discountAmount > 0 && <div><span>Discount</span><strong>₹{b.discountAmount}</strong></div>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Load more */}
      <div className="bk_loadmore">
        {isLoadMoreData ? (
          <i className="fas fa-spinner fa-pulse fa-2x"></i>
        ) : noMoreData ? (
          <p className="bk_nomore">No more bookings</p>
        ) : (
          <button className="bk_loadmore_btn" onClick={handleShowMore}>Load More</button>
        )}
      </div>
    </div>
  );
};

const mapDisptachToProps = (dispatch) => ({
  setOnTodayBooking: (p) => dispatch(settodaybooking(p)),
});

export default connect(null, mapDisptachToProps)(TodayBookingList);

